/**
 * Outage status → semantic colour token mapping.
 * Token names use the status-* prefix per packages/branding/src/tokens.ts.
 * NOT semantic-* — that prefix does not exist on disk.
 */

export type OutageStatus =
  | "ACTIVE_NO_ETA"
  | "ACTIVE_WITH_ETA"
  | "SCHEDULED"
  | "RESOLVED";

/**
 * The statuses that count as a LIVE service interruption (R-P3-4).
 *
 * SCHEDULED is excluded deliberately: planned maintenance with a future window is
 * not "the power is out". RESOLVED is excluded for the obvious reason. This is the
 * definition web's home has always used; mobile's home pill and map header each
 * rolled their own `status !== "RESOLVED"` filter, which silently counted
 * SCHEDULED work as an outage — so one fixture set produced three different
 * answers to "how many outages are there?" across the two surfaces.
 */
export const ACTIVE_OUTAGE_STATUSES: readonly OutageStatus[] = [
  "ACTIVE_NO_ETA",
  "ACTIVE_WITH_ETA",
];

/** True when an outage is a live interruption. See ACTIVE_OUTAGE_STATUSES. */
export function isActiveOutage(outage: { status: string }): boolean {
  return (ACTIVE_OUTAGE_STATUSES as readonly string[]).includes(outage.status);
}

/**
 * THE active-outage count. Both surfaces derive every "N outages" figure from
 * this one function so the number can never diverge again.
 *
 * SCOPE IS THE CALLER'S: pass the island-wide seed set for a network-status
 * figure, or a circuit-filtered set for a persona-scoped one. A persona-scoped
 * count MUST be labelled as such — an unlabelled count that silently means
 * something narrower than the reader assumes is the defect this replaced.
 */
export function countActiveOutages(outages: readonly { status: string }[]): number {
  return outages.filter(isActiveOutage).length;
}

// `dot` is the solid status colour for the list-view leading dot (a large
// graphic at 3:1, so it stays on the base fill tokens — D4 large-graphic rule).
// Added so web's OutageListView converges here instead of forking its own map
// (D5 cross-surface parity); mobile consumers that read only {bg,text,label}
// are unaffected (additive field).
//
// `label` is the English SSoT (mobile + any not-yet-migrated surface); `labelKey`
// resolves via t() for the trilingual web outage surfaces (D5 status LABELS join
// outage.json — mirrors the SR_STATUS_TOKENS / PAYMENT_STATUS_TOKENS precedent).
// Additive: one place defines both the English string and its key, so list,
// detail, and timeline can never drift on the outage status vocabulary.
export const OUTAGE_STATUS_TOKENS: Record<
  OutageStatus,
  { bg: string; text: string; label: string; labelKey: string; dot: string }
> = {
  ACTIVE_NO_ETA: {
    bg: "bg-status-danger/10",
    text: "text-status-danger",
    label: "Active — no ETA",
    labelKey: "outageStatus.activeNoEta",
    dot: "bg-status-danger",
  },
  ACTIVE_WITH_ETA: {
    bg: "bg-status-warning/10",
    // AA text (N-4 sibling-sweep): amber-800 for label TEXT, per the branding
    // token comment — same fix as the SR in-review tag.
    text: "text-status-warning-text",
    label: "Active — crew en route",
    labelKey: "outageStatus.activeWithEta",
    dot: "bg-status-warning",
  },
  SCHEDULED: {
    bg: "bg-status-planned/10",
    text: "text-status-planned",
    label: "Scheduled maintenance",
    labelKey: "outageStatus.scheduled",
    dot: "bg-status-planned",
  },
  RESOLVED: {
    bg: "bg-status-success/10",
    // AA text (O-R1/D4): status-success (#1A8754) is ~4.0:1 as small label
    // text; status-success-text (#116A42) is the darkened AA sibling.
    text: "text-status-success-text",
    label: "Resolved",
    labelKey: "outageStatus.resolved",
    dot: "bg-status-success",
  },
};
